import { ApolloError } from '@apollo/client';

import { useErpCareerMemberDetailQuery } from 'core/graphql';

import { ICareerMemberDetail, ICareerProfile } from './careerModel';

interface ICareerMemberDetailHook {
  detail?: ICareerMemberDetail;
  profile?: ICareerProfile;
  loading: boolean;
  error?: ApolloError;
}

/**
 * Spec 0018 increment B1 — one member's full card, read-only in ERP. Fetches
 * `careerMemberDetail` (member header + nested structured `profile`) for the
 * currently opened member.
 *
 * `id` is optional: while no member is selected the caller passes `undefined`
 * and the query is skipped, so no request fires until the operator opens a row.
 * `profile` is surfaced separately because it is nullable — a member who has
 * not built a profile yet reads back `null`, which the panel renders as the
 * muted empty state.
 */
export const useCareerMemberDetail = (id?: string): ICareerMemberDetailHook => {
  const { data, loading, error } = useErpCareerMemberDetailQuery({
    variables: { id: id ?? '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const detail = data?.careerMemberDetail ?? undefined;

  return {
    detail,
    profile: detail?.profile ?? undefined,
    loading,
    error,
  };
};
